import React, { useEffect, useState, useCallback, memo, useMemo } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { Shield, Brain, Users, ArrowRight, Zap, Eye, Lock, Cpu, Network, Activity } from 'lucide-react';
import OptimizedImage from './ui/OptimizedImage';

const networkNodes = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 2,
}));

const securityIcons = [Shield, Lock, Eye, Cpu, Network, Activity];

const stats = [
  { icon: Shield, value: '95%', label: 'Risk Tespiti' },
  { icon: Brain, value: '40%', label: 'Daha Etkili' },
  { icon: Users, value: '250+', label: 'Hedef Müşteri' },
];

const CogniSafeHero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const controls = useAnimationControls();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) / window.innerWidth,
        y: (e.clientY - window.innerHeight / 2) / window.innerHeight,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = useCallback((href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Memoize background grid style
  const backgroundGridStyle = useMemo(() => ({
    backgroundImage: `
      linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
    `,
    backgroundSize: '50px 50px',
    transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`,
  }), [mousePosition.x, mousePosition.y]);
  

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden hero-gradient">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={backgroundGridStyle} />
      </div>

      {/* Neural Network Visualization */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="w-full h-full" viewBox="0 0 1000 1000">
          <defs>
            <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#1e40af" stopOpacity="0.1" />
            </radialGradient>
          </defs>
          
          {/* Neural connections */}
          {networkNodes.map((node, i) => 
            networkNodes.slice(i + 1).map((targetNode, j) => {
              const distance = Math.sqrt(
                Math.pow(node.x - targetNode.x, 2) + Math.pow(node.y - targetNode.y, 2)
              );
              if (distance < 40) {
                return (
                  <motion.line
                    key={`${i}-${j}`}
                    x1={`${node.x * 10}px`}
                    y1={`${node.y * 10}px`}
                    x2={`${targetNode.x * 10}px`}
                    y2={`${targetNode.y * 10}px`}
                    stroke="url(#nodeGradient)"
                    strokeWidth="1"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.1 }}
                    transition={{
                      duration: 3,
                      delay: Math.max(node.delay, targetNode.delay),
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "linear",
                    }}
                  />
                );
              }
              return null;
            })
          )}

          {/* Neural nodes */}
          {networkNodes.map((node, i) => (
            <motion.circle
              key={i}
              cx={`${node.x * 10}px`}
              cy={`${node.y * 10}px`}
              r="4"
              fill="url(#nodeGradient)"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.3, 0.1] }}
              transition={{
                duration: 4,
                delay: node.delay,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          ))}
        </svg>
      </div>

      {/* Floating Security Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {securityIcons.map((Icon, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${10 + (i * 15)}%`,
              top: `${20 + (i * 10)}%`,
            }}
            animate={{
              y: [-10, 10, -10],
              rotate: [0, 2, 0, -2, 0],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: 6 + i,
              repeat: Infinity,
              ease: "linear",
              delay: i * 0.5,
            }}
          >
            <div className="p-4 bg-white/60 rounded-2xl backdrop-blur-sm border border-blue-200/30 shadow-sm">
              <Icon className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-8"
          >


            {/* Main Headline */}
            <div className="space-y-6">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-5xl lg:text-7xl font-heading font-black leading-tight"
              >
                <span className="block text-gray-800">İnsan Faktörünü</span>
                <span className="block bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                  Güvenliğin Gücüne
                </span>
                <span className="block text-gray-800">Dönüştürüyoruz</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-xl font-sans text-gray-600 leading-relaxed max-w-lg"
              >
                Yapay zeka ve davranış bilimiyle çalışanların siber güvenlik farkındalığını artıran, 
                riskleri öngören ve proaktif koruma sağlayan devrim niteliğinde platform.
              </motion.p>
            </div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollToSection('#contact')}
                className="group trust-button relative px-8 py-4 rounded-2xl font-sans font-bold text-white text-lg overflow-hidden transition-all duration-300 hover:scale-105"
              >
                <span className="relative z-10 flex items-center">
                  Ücretsiz Demo
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>

              <button
                onClick={() => scrollToSection('#features')}
                className="px-8 py-4 border-2 border-blue-500 text-blue-600 bg-white rounded-2xl font-sans font-bold text-lg hover:bg-blue-50 transition-all duration-300 hover:scale-105 shadow-sm"
              >
                Özellikleri Keşfet
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="grid grid-cols-3 gap-6 pt-8"
            >
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-3 shadow-sm">
                    <stat.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-2xl font-heading font-bold text-gray-800 mb-1 tabular-nums">{stat.value}</div>
                  <div className="text-sm font-sans text-gray-500">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Visual Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
            className="relative lg:block hidden"
          >
            {/* Main Visualization Container */}
            <div className="relative w-96 h-96 mx-auto">
              
              {/* Central Brain/AI Core */}
              <motion.div
                className="absolute inset-16 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-full border-2 border-blue-400/30 backdrop-blur-sm"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <div className="absolute inset-4 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center shadow-2xl shadow-blue-500/50">
                  <Brain className="w-16 h-16 text-white" />
                </div>
              </motion.div>

              {/* Orbiting Security Elements */}
              {[Shield, Lock, Eye, Zap].map((Icon, i) => (
                <motion.div
                  key={i}
                  className="absolute w-16 h-16 bg-slate-800/80 rounded-2xl border border-blue-400/30 backdrop-blur-sm flex items-center justify-center shadow-lg"
                  style={{
                    left: '50%',
                    top: '50%',
                    transformOrigin: '0 0',
                  }}
                  animate={{
                    rotate: 360,
                    x: Math.cos((i * Math.PI) / 2) * 150 - 32,
                    y: Math.sin((i * Math.PI) / 2) * 150 - 32,
                  }}
                  transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.5,
                  }}
                >
                  <Icon className="w-8 h-8 text-blue-400" />
                </motion.div>
              ))}

              {/* Data Flow Lines */}
              <svg className="absolute inset-0 w-full h-full">
                <defs>
                  <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
                    <stop offset="50%" stopColor="#06b6d4" stopOpacity="1" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>
                
                {Array.from({ length: 8 }).map((_, i) => (
                  <motion.circle
                    key={i}
                    r="150"
                    cx="192"
                    cy="192"
                    fill="none"
                    stroke="url(#flowGradient)"
                    strokeWidth="2"
                    strokeDasharray="20 10"
                    initial={{ strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: -100 }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                      delay: i * 0.2,
                    }}
                    style={{ opacity: 0.3 }}
                  />
                ))}
              </svg>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={() => scrollToSection('#features')}
      >
        <div className="w-6 h-10 border-2 border-blue-400 rounded-full flex justify-center relative overflow-hidden">
          <motion.div
            className="w-1 h-3 bg-gradient-to-b from-blue-400 to-cyan-400 rounded-full mt-2"
            animate={{ y: [0, 16, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>

      {/* Ambient Light Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl"></div>
    </section>
  );
};

export default memo(CogniSafeHero);